import { pathWithQuery } from "@/lib/routes"
import { ATTRIBUTE_PARAM_ALIASES, ATTRIBUTE_PARAM_PREFIX, SEARCH_PARAM_KEYS } from "@/lib/types/search"

const LISTING_PAGE_PARAM = "pagina"

const MAX_LISTING_PAGE = 1000

export type ListingSearchParams = Record<string, string | string[] | undefined>

export const listingPageNumber = (searchParams: ListingSearchParams): number => {
  const raw = searchParams[LISTING_PAGE_PARAM]
  const value = Array.isArray(raw) ? raw[0] : raw
  if (!value || !/^[1-9]\d{0,3}$/.test(value)) return 1
  const page = Number(value)
  return page <= MAX_LISTING_PAGE ? page : 1
}

export const withoutListingPage = (searchParams: ListingSearchParams): ListingSearchParams =>
  Object.fromEntries(Object.entries(searchParams).filter(([key]) => key !== LISTING_PAGE_PARAM))

export const listingPageFromQueryString = (queryString: string): number =>
  listingPageNumber({ [LISTING_PAGE_PARAM]: new URLSearchParams(queryString).get(LISTING_PAGE_PARAM) ?? undefined })

const LISTING_PARAM_KEYS = new Set<string>([
  ...Object.values(SEARCH_PARAM_KEYS),
  ...Object.values(ATTRIBUTE_PARAM_ALIASES).filter((alias): alias is string => Boolean(alias)),
  LISTING_PAGE_PARAM,
])

export const isListingParam = (key: string): boolean => LISTING_PARAM_KEYS.has(key) || key.startsWith(ATTRIBUTE_PARAM_PREFIX)

export const listingParamsOnly = (queryString: string): string => {
  const params = new URLSearchParams(queryString)
  for (const key of [...params.keys()]) if (!isListingParam(key)) params.delete(key)
  return params.toString()
}

const PAGE_LABEL_SEPARATOR = " · "

export const pagedTitle = (title: string, pageLabel: string): string => {
  const brandAt = title.lastIndexOf(" | ")
  return brandAt > 0 ? `${title.slice(0, brandAt)}${PAGE_LABEL_SEPARATOR}${pageLabel}${title.slice(brandAt)}` : `${title}${PAGE_LABEL_SEPARATOR}${pageLabel}`
}

export const unpagedTitle = (title: string): string => title.replace(/ · [^|·]*\d+(?= \| |$)/, "")

export const listingPageCount = (totalCount: number, pageSize: number): number => (pageSize > 0 ? Math.min(MAX_LISTING_PAGE, Math.max(1, Math.ceil(totalCount / pageSize))) : 1)

export type PageWindowItem = number | "gap"

const PAGE_WINDOW_RADIUS = 2

export const pageWindow = (current: number, last: number): PageWindowItem[] => {
  const anchors = new Set<number>([1, last])
  for (let page = current - PAGE_WINDOW_RADIUS; page <= current + PAGE_WINDOW_RADIUS; page += 1) if (page >= 1 && page <= last) anchors.add(page)
  const sorted = [...anchors].sort((a, b) => a - b)
  return sorted.flatMap((page, index): PageWindowItem[] => {
    const previous = sorted[index - 1]
    if (previous === undefined || page - previous === 1) return [page]
    if (page - previous === 2) return [previous + 1, page]
    return ["gap", page]
  })
}

export const listingPageHref = (pathname: string, queryString: string, page: number): string => {
  const params = new URLSearchParams(queryString)
  if (page > 1) params.set(LISTING_PAGE_PARAM, String(page))
  else params.delete(LISTING_PAGE_PARAM)
  return pathWithQuery(pathname, params.toString())
}

export interface ListingPageLink {
  page: number
  href: string
  current: boolean
}

export interface ListingPagination {
  offset: number
  nextHref?: string
  links: (ListingPageLink | "gap")[]
}

export const buildListingPagination = ({
  pathname,
  queryString,
  page,
  totalCount,
  pageSize,
}: {
  pathname: string
  queryString: string
  page: number
  totalCount: number
  pageSize: number
}): ListingPagination | undefined => {
  const pageCount = listingPageCount(totalCount, pageSize)
  if (pageCount <= 1 || page > pageCount) return undefined
  const hrefFor = (target: number) => listingPageHref(pathname, queryString, target)
  return {
    offset: (page - 1) * pageSize,
    nextHref: page < pageCount ? hrefFor(page + 1) : undefined,
    links: pageWindow(page, pageCount).map((item) => (item === "gap" ? item : { page: item, href: hrefFor(item), current: item === page })),
  }
}
